import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { extractErrorMessage } from "../../utils/errorMessage";

// Must match the backend assignmentHistory.condition enum (stored lowercase).
const CONDITIONS = ["Excellent", "Good", "Fair", "Poor", "Damaged"];

export default function AssignInventoryToEmployeeNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    itemID: null,
    assignedTo: null,
    assignedBy: user?.employeeid || "",
    assignmentDate: new Date().toISOString().slice(0, 10),
    returnDate: "",
    condition: "Good",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/api/inventory/unassigned"),
      api.get(endpoints.employees.list),
    ]).then(([a, b]) => {
      setItems(Array.isArray(a.data) ? a.data : a.data?.data || []);
      setEmployees(Array.isArray(b.data) ? b.data : b.data?.data || []);
    }).catch((err) => toast.error(extractErrorMessage(err)));
  }, []);

  const itemOpts = useMemo(
    () => items
      .map((i) => ({ id: i.itemID, label: `${i.itemName} (${i.itemID})`, meta: i }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [items]
  );
  const empOpts = useMemo(
    () => employees
      .map((e) => ({ id: e.employeeid, label: `${e.name} (${e.employeeid})`, meta: e }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [employees]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.itemID || !form.assignedTo) return toast.error("Item and employee required");
    setLoading(true);
    try {
      await api.post("/api/inventory/assign-inventory", {
        itemID: form.itemID.id,
        assignedTo: form.assignedTo.id,
        assignedBy: form.assignedBy,
        assignmentDate: form.assignmentDate,
        returnDate: form.returnDate || null,
        condition: form.condition,
      });
      toast.success("Item assigned");
      nav("/InventoryList");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  const selectedItem = form.itemID?.meta;
  const selectedEmp = form.assignedTo?.meta;

  return (
    <Box p={3}>
      <PageHeader
        title="Assign Inventory"
        subtitle="Hand over an item to an employee"
        icon={<PersonAddAlt1OutlinedIcon />}
        actions={[<Button key="back" startIcon={<ArrowBackIcon />} onClick={() => nav(-1)}>Back</Button>]}
      />

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionCard title="Assignment Details" accent="#6C50BF">
              <Stack spacing={2.5}>
                <Autocomplete
                  options={itemOpts}
                  value={form.itemID}
                  onChange={(_, v) => setForm((f) => ({ ...f, itemID: v }))}
                  getOptionLabel={(o) => o?.label || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(p) => <TextField {...p} label="Select Unassigned Item *" required />}
                />
                <Autocomplete
                  options={empOpts}
                  value={form.assignedTo}
                  onChange={(_, v) => setForm((f) => ({ ...f, assignedTo: v }))}
                  getOptionLabel={(o) => o?.label || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(p) => <TextField {...p} label="Assign To *" required />}
                />
                <TextField fullWidth label="Assigned By" value={form.assignedBy} onChange={(e) => setForm((f) => ({ ...f, assignedBy: e.target.value }))} helperText="Your employee ID (auto-filled)" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Assignment Date" InputLabelProps={{ shrink: true }} value={form.assignmentDate} onChange={(e) => setForm((f) => ({ ...f, assignmentDate: e.target.value }))} required /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Expected Return (optional)" InputLabelProps={{ shrink: true }} value={form.returnDate} onChange={(e) => setForm((f) => ({ ...f, returnDate: e.target.value }))} inputProps={{ min: form.assignmentDate }} /></Grid>
                </Grid>
                <TextField fullWidth select label="Condition at Handover" value={form.condition} onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}>
                  {CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
              <SectionCard title="Preview" accent="#F9BC06">
                <Stack spacing={2}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5, mb: 0.5 }}>Item</Typography>
                    {selectedItem ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Inventory2OutlinedIcon sx={{ color: "primary.main" }} />
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{selectedItem.itemName}</Typography>
                          <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: "monospace" }}>{selectedItem.itemID} • {selectedItem.category}</Typography>
                        </Box>
                      </Stack>
                    ) : <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Select an item</Typography>}
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5, mb: 0.5 }}>Employee</Typography>
                    {selectedEmp ? (
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{selectedEmp.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{selectedEmp.employeeid} • {selectedEmp.department || selectedEmp.designation}</Typography>
                      </Box>
                    ) : <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Select employee</Typography>}
                  </Box>
                  <Chip label={form.condition} sx={{ bgcolor: "#EDFAF2", color: "#39C645", fontWeight: 700, alignSelf: "flex-start" }} />
                  <Button
                    type="submit" variant="contained" fullWidth size="large"
                    disabled={loading || !form.itemID || !form.assignedTo}
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
                  >
                    {loading ? "Assigning…" : "Confirm Assignment"}
                  </Button>
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
